import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress,
  Divider, MenuItem, Paper, Stack,
  TextField, Typography, alpha, useTheme,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import { GoalsIcon } from '../global/CustomIcons';
import FlagIcon from '@mui/icons-material/Flag';
import TimelineIcon from '@mui/icons-material/Timeline';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import AddTaskIcon from '@mui/icons-material/AddTask';
import TuneIcon from '@mui/icons-material/Tune';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import PatternIcon from '@mui/icons-material/Pattern';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import { fetchRequest as fetchOverview } from '../../redux/actions/signals/overview';
import { fetchRequest as fetchGroups } from '../../redux/actions/signals/groups';
import { fetchRequest as createGoalRequest, clearState as clearCreateGoal } from '../../redux/actions/discovery/createGoal';
import { fetchRequest as fetchPrice } from '../../redux/actions/discovery/price';
import { fetchRequest as fetchProfilePreview, clearState as clearProfilePreview } from '../../redux/actions/discovery/profilePreview';
import { fetchRequest as fetchFleetPreview, clearState as clearFleetPreview } from '../../redux/actions/discovery/fleetPreview';
import { fetchRequest as fetchModels } from '../../redux/actions/discovery/models';
import { fetchRequest as fetchBlueprints } from '../../redux/actions/blueprints/list';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import CostEstimateCard, {
  BreakdownRow, ExplainSectionTitle,
} from '../billing/CostEstimateCard';
import {
  CADENCES, FREQUENCIES, DISCOVERY_MODES, RETRAIN_MODES, SERVING_MODES,
  BUDGET_LABEL, USER_MODEL_COST_WEIGHT,
  HORIZON_MIN, HORIZON_MAX, LAG_MIN, LAG_MAX,
  goalCopy, fmtSpan, SpanPicker, ModelLineupPicker,
} from './goalControls';
import { modelDisplayName } from './modelNames';

const GOAL_TYPES = [
  { id: 'forecast_value', label: 'Forecast a value', icon: <TimelineIcon fontSize="small" />, blurb: 'Predict a future number.', accentKey: 'primary' },
  { id: 'forecast_distribution', label: 'Forecast a range', icon: <StackedLineChartIcon fontSize="small" />, blurb: 'Predict a likely band.', accentKey: 'info' },
  { id: 'trend', label: 'Trend', icon: <TrendingUpIcon fontSize="small" />, blurb: 'Predict up or down.', accentKey: 'success' },
  { id: 'anomaly', label: 'Anomaly', icon: <CrisisAlertIcon fontSize="small" />, blurb: 'What precedes spikes.', accentKey: 'warning' },
];

// Fleet (population) goal types — "rank my fleet by X".
const FLEET_GOAL_TYPES = [
  {
    id: 'rank_by_risk',
    label: 'Rank by risk',
    icon: <CrisisAlertIcon fontSize="small" />,
    blurb: 'Which members will breach their threshold — sensors about to fail, queues about to overflow.',
    accentKey: 'warning',
  },
  {
    id: 'rank_by_growth',
    label: 'Rank by growth',
    icon: <TrendingUpIcon fontSize="small" />,
    blurb: 'Which members will move the most — SKUs about to outperform, tickers about to run.',
    accentKey: 'success',
  },
];

// What the goal watches: one series, or a whole population of them.
const SCOPES = [
  {
    id: 'single',
    label: 'One signal',
    icon: <ShowChartIcon fontSize="small" />,
    blurb: 'Forecast or explain a single series — drivers, models, live predictions.',
  },
  {
    id: 'fleet',
    label: 'A fleet of signals',
    icon: <LeaderboardIcon fontSize="small" />,
    blurb: 'Watch a whole population and rank WHICH members matter — a live leaderboard.',
  },
];

const SELECTOR_TYPES = [
  {
    id: 'group',
    label: 'Signal group',
    icon: <WorkspacesIcon fontSize="small" />,
    blurb: 'Every signal in one of your groups.',
  },
  {
    id: 'pattern',
    label: 'Key pattern',
    icon: <PatternIcon fontSize="small" />,
    blurb: 'Every key matching a glob, e.g. sensor_*_vibration.',
  },
];

// Fleet scoring cadences (runs are batched; sub-15-minute loops are noise).
const RUN_CADENCES = CADENCES.filter((c) => c.value >= 900);

const CreateGoalPage = ({
  signals, signalsLoading, fetchSignals,
  groups, groupsLoading, groupsFetch,
  createGoal, createState, clearCreate,
  price, priceFetch,
  preview, previewFetch, previewClear,
  fleetPreview, fleetPreviewFetch, fleetPreviewClear,
  models, modelsLoading, modelsFetch,
  blueprints, blueprintsLoading, blueprintsFetch,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetTarget = searchParams.get('target') || null;
  const presetKind = searchParams.get('kind') === 'fleet' ? 'fleet' : 'single';

  // Scope: 'single' (one target signal) or 'fleet' (a population).
  const [scope, setScope] = useState(presetKind);
  const [name, setName] = useState('');
  const [goalType, setGoalType] = useState(null);

  // --- Single-signal state -------------------------------------------------
  const [targetKey, setTargetKey] = useState(presetTarget);
  const [deselected, setDeselected] = useState(() => new Set());
  const [bpSelected, setBpSelected] = useState(() => new Set());
  const [intervalOverride, setIntervalOverride] = useState(null);
  const [horizonOverride, setHorizonOverride] = useState(null);
  const [maxLagOverride, setMaxLagOverride] = useState(null);
  // Automation policies — auto is the default for all three actions.
  const [discoveryMode, setDiscoveryMode] = useState('auto');
  const [discoveryFreq, setDiscoveryFreq] = useState(7 * 86400);
  const [retrainMode, setRetrainMode] = useState('auto');
  const [retrainFreq, setRetrainFreq] = useState(7 * 86400);
  const [servingMode, setServingMode] = useState('auto');
  const [servingFreq, setServingFreq] = useState(86400);
  const [monthlyBudget, setMonthlyBudget] = useState('');

  // --- Fleet state ---------------------------------------------------------
  const [selectorType, setSelectorType] = useState('group');
  const [selectorValue, setSelectorValue] = useState('');
  const [fleetInterval, setFleetInterval] = useState(3600);
  const [fleetHorizon, setFleetHorizon] = useState(12);
  const [runInterval, setRunInterval] = useState(null); // null = every data bin
  const [riskDirection, setRiskDirection] = useState('above');
  const [thresholdMode, setThresholdMode] = useState('auto');
  const [thresholdValue, setThresholdValue] = useState('');

  const isFleet = scope === 'fleet';

  useEffect(() => { fetchSignals(); priceFetch(); }, [fetchSignals, priceFetch]);
  useEffect(() => () => { clearCreate(); previewClear(); fleetPreviewClear(); },
    [clearCreate, previewClear, fleetPreviewClear]);

  // Groups feed the fleet population picker — fetched once fleet is chosen.
  useEffect(() => { if (isFleet) groupsFetch(); }, [isFleet, groupsFetch]);

  // The two goal-type vocabularies don't overlap — a switch resets the pick.
  const switchScope = (next) => {
    if (next === scope) return;
    setScope(next);
    setGoalType(null);
    clearCreate();
  };

  useEffect(() => {
    if (!isFleet && targetKey) previewFetch({ target_key: targetKey, goal_type: goalType || undefined });
    else previewClear();
  }, [isFleet, targetKey, goalType, previewFetch, previewClear]);

  // Live fleet membership preview — debounced so pattern typing doesn't spam.
  useEffect(() => {
    if (!isFleet || !selectorValue.trim()) { fleetPreviewClear(); return undefined; }
    const t = setTimeout(() => {
      fleetPreviewFetch({ selectorType, selectorValue: selectorValue.trim() });
    }, 350);
    return () => clearTimeout(t);
  }, [isFleet, selectorType, selectorValue, fleetPreviewFetch, fleetPreviewClear]);

  useEffect(() => {
    if (!isFleet && goalType) {
      modelsFetch({ goalType });
      setDeselected(new Set());
      setBpSelected(new Set());
    }
  }, [isFleet, goalType, modelsFetch]);

  useEffect(() => {
    const resp = createState?.response;
    if (!resp?.goal_id) return;
    if (resp.goal_kind === 'fleet') navigate(`/goals/${resp.goal_id}`);
    else navigate(`/goals/${resp.goal_id}?run=${resp.run_id || ''}`);
  }, [createState?.response, navigate]);

  const targetOptions = useMemo(
    () => (signals || []).filter((s) => s?.type !== 'event' && s?.key).map((s) => s.key),
    [signals],
  );
  const targetLabels = useMemo(() => {
    const m = {};
    (signals || []).forEach((s) => { if (s?.key && s?.display_name) m[s.key] = s.display_name; });
    return m;
  }, [signals]);
  const labelFor = (k) => targetLabels[k] || k || '';

  const groupOptions = useMemo(
    () => (groups || []).map((grp) => grp.name).filter((n) => n && n !== 'other'),
    [groups],
  );

  const relevantModels = useMemo(() => (models || []).filter((m) => m.relevant), [models]);
  const selectedModels = useMemo(
    () => relevantModels.filter((m) => m.always_on || !deselected.has(m.id)),
    [relevantModels, deselected],
  );

  // Selection changes from the shared picker arrive as the next two sets.
  const onModelsChange = ({ deselected: nextDes, bpSelected: nextBp }) => {
    setDeselected(nextDes);
    setBpSelected(nextBp);
  };

  const quotaBlocked = price && price.goals_limit !== -1 && price.goals_used >= price.goals_limit;
  const runCost = price?.run_cost ?? 0;
  const baseFitCost = price?.tournament_cost ?? 0;
  // Fit cost scales with the chosen models, weighted by their relative
  // compute cost — mirrors model_library.selection_cost_factor on the backend.
  const selectionFactor = useMemo(() => {
    const full = relevantModels.reduce((s, m) => s + (m.cost_weight || 1), 0);
    if (full <= 0) return 1;
    const sel = selectedModels.reduce((s, m) => s + (m.cost_weight || 1), 0);
    const publicFactor = Math.min(1, sel / full);
    // Your own models re-fit per walk-forward fold — priced like the heavy
    // foundation tier (mirrors model_library.USER_MODEL_COST_WEIGHT on the
    // backend). They add compute on top of the public lineup, so the factor
    // can exceed 1.
    const userWeight = bpSelected.size * USER_MODEL_COST_WEIGHT;
    return Math.max(0.001, publicFactor + userWeight / full);
  }, [relevantModels, selectedModels, bpSelected]);
  const fitCost = Math.round(baseFitCost * selectionFactor);

  const resolved = preview?.response?.resolved;
  // Effective cadence/horizon/lag (override falls back to auto-detected).
  const effInterval = intervalOverride ?? resolved?.interval_seconds ?? 60;
  const effHorizon = horizonOverride ?? resolved?.horizon_bins ?? 12;
  const effLag = maxLagOverride ?? resolved?.max_lag_bins ?? 48;

  // Cost model (DISCOVERY_AUTOPILOT_PLAN.md cost map + shared/dedicated
  // serving policy):
  //  • Setup (charged today): discovery scan + first tournament fit.
  //  • Recurring monthly: live monitoring (flat per-day meter for
  //    monitoring/revalidation/shocks) + live predictions + one automated
  //    re-discovery + auto-retraining (usage-based, bounded by the cap).
  //  • Live predictions default to pay-per-prediction on SHARED infra; a
  //    a DEDICATED reservation swaps that for a flat hosting fee
  //    with all predictions included.
  const setupCost = runCost + fitCost;
  const liveDayCost = price?.live_day_cost ?? 0;
  const retrainCost = price?.retrain_cost ?? 0;
  const inferenceCost = price?.inference_cost ?? 0;
  const hostingDayCost = price?.hosting_day_cost ?? 0;
  const liveMonthly = Math.round(liveDayCost * 30);
  const rediscoverMonthly = runCost * (price?.rediscovery_per_month ?? 1);

  // Champions re-forecast once per data cadence.
  const SECONDS_PER_MONTH = 30 * 86400;
  const serveEverySec = effInterval;
  const predictionsPerMonth = Math.round(SECONDS_PER_MONTH / serveEverySec);
  const sharedPredictMonthly = Math.round(predictionsPerMonth * inferenceCost);
  const dedicatedPredictMonthly = Math.round(hostingDayCost * 30);
  const dedicatedWorthIt = dedicatedPredictMonthly < sharedPredictMonthly;

  // Headline follows the recommended (cheaper) serving option at this
  // cadence — pay-per-prediction when volume is low, a dedicated deployment
  // when it wins on price.
  const serveMonthly = dedicatedWorthIt ? dedicatedPredictMonthly : sharedPredictMonthly;
  const monthlyRecurring = liveMonthly + serveMonthly + rediscoverMonthly;
  const retrainCap = monthlyBudget !== '' && Number(monthlyBudget) > 0
    ? Number(monthlyBudget)
    : (price?.default_monthly_cap ?? 0);
  // Balance must cover what's actually charged now (the setup), not the
  // recurring estimate.
  const insufficient = price && price.balance != null && price.balance < setupCost;
  const notAnalyzable = Boolean(preview?.error);
  const meteredSelected = selectedModels.some((m) => m.metered);
  const singleReady = Boolean(targetKey && goalType);
  const canStartSingle = singleReady && !createState?.isLoading && !quotaBlocked && !insufficient && !notAnalyzable;

  // --- Fleet economics -----------------------------------------------------
  const fleetPreviewData = fleetPreview?.response;
  const memberCount = fleetPreviewData?.member_count ?? null;
  const overLimit = fleetPreviewData && fleetPreviewData.within_limit === false;
  const bakeoffCost = fleetPreviewData?.bakeoff_cost ?? 0;
  const fleetRunCost = fleetPreviewData?.run_cost ?? 0;

  // Recurring: one scoring run per cadence slot.
  const runEverySec = runInterval || fleetInterval;
  const runsPerMonth = Math.max(1, Math.round(SECONDS_PER_MONTH / Math.max(runEverySec, 900)));
  const fleetMonthly = Math.round(fleetRunCost * runsPerMonth);

  const fleetInsufficient = price && price.balance != null && price.balance < bakeoffCost;
  const fleetReady = Boolean(selectorValue.trim() && goalType && memberCount > 0 && !overLimit);
  const canStartFleet = fleetReady && !createState?.isLoading && !quotaBlocked && !fleetInsufficient;

  const ready = isFleet ? fleetReady : singleReady;
  const canStart = isFleet ? canStartFleet : canStartSingle;

  // Has the user changed any tuning default? Drives a single "Reset" affordance.
  const tuningDirty = (
    intervalOverride != null ||
    horizonOverride != null ||
    maxLagOverride != null ||
    discoveryMode !== 'auto' ||
    retrainMode !== 'auto' ||
    servingMode !== 'auto' ||
    monthlyBudget !== ''
  );
  const resetTuning = () => {
    setIntervalOverride(null);
    setHorizonOverride(null);
    setMaxLagOverride(null);
    setDiscoveryMode('auto');
    setRetrainMode('auto');
    setServingMode('auto');
    setMonthlyBudget('');
  };

  // A policy is {mode} plus, for scheduled, the chosen cadence.
  const policyPayload = (mode, freq) =>
    (mode === 'scheduled' ? { mode, frequency_seconds: freq } : { mode });

  const submitSingle = () => {
    const options = {
      discovery_policy: policyPayload(discoveryMode, discoveryFreq),
      retrain_policy: policyPayload(retrainMode, retrainFreq),
      serving_policy: policyPayload(servingMode, servingFreq),
      live: true,
    };
    if (maxLagOverride != null) options.max_lag_bins = maxLagOverride;
    // Goals discover drivers then auto-fit and auto-deploy the best model —
    // no manual gating. Tuning below only adjusts cadence/horizon.
    options.pipeline = { auto_fit: true, auto_deploy: 'auto' };
    options.autopilot = 'full';
    const cap = Number(monthlyBudget);
    if (monthlyBudget !== '' && Number.isFinite(cap) && cap > 0) options.monthly_spend_cap = cap;
    const ms = {};
    const exclude = [...deselected];
    if (exclude.length) ms.exclude = exclude;
    const bpIds = [...bpSelected];
    if (bpIds.length) ms.user_blueprints = bpIds;
    if (Object.keys(ms).length) options.model_selection = ms;
    createGoal({
      name: name.trim() || undefined,
      target_key: targetKey,
      goal_type: goalType,
      ...(intervalOverride != null ? { interval_seconds: intervalOverride } : {}),
      ...(horizonOverride != null ? { horizon_bins: horizonOverride } : {}),
      options,
    });
  };

  const submitFleet = () => {
    const options = { live: true };
    const fleet = {};
    if (runInterval) fleet.run_interval_seconds = runInterval;
    if (goalType === 'rank_by_risk') {
      const risk = { direction: riskDirection };
      if (thresholdMode === 'absolute' && thresholdValue !== '') {
        risk.threshold = { mode: 'absolute', value: Number(thresholdValue) };
      }
      fleet.risk = risk;
    }
    if (Object.keys(fleet).length) options.fleet = fleet;
    createGoal({
      goal_kind: 'fleet',
      name: name.trim() || undefined,
      goal_type: goalType,
      target_selector: { type: selectorType, value: selectorValue.trim() },
      interval_seconds: fleetInterval,
      horizon_bins: fleetHorizon,
      options,
    });
  };

  const submit = () => (isFleet ? submitFleet() : submitSingle());

  const accent = (key) => theme.palette[key]?.main || theme.palette.primary.main;

  const CardHead = ({ icon, title, helper }) => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      </Stack>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          {helper}
        </Typography>
      )}
    </Box>
  );

  const SelectCard = ({ color, icon, dot, title, tagline, description, active, onClick, disabled, right }) => (
    <Box
      onClick={disabled ? undefined : onClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        position: 'relative',
        p: 1.25,
        borderRadius: 2,
        border: `1.5px solid ${active ? color : theme.palette.divider}`,
        bgcolor: active ? alpha(color, 0.1) : 'transparent',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 120ms ease',
        '&:hover': disabled ? {} : { borderColor: color, bgcolor: alpha(color, 0.06) },
      }}
    >
      {active && <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, fontSize: 16, color }} />}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: tagline || description ? 0.25 : 0, pr: active ? 2.5 : 0 }}>
        {icon
          ? <Box sx={{ display: 'flex', color }}>{icon}</Box>
          : dot && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />}
        <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{title}</Typography>
        {right}
      </Stack>
      {tagline && <Typography variant="caption" sx={{ color, fontWeight: 600, display: 'block' }}>{tagline}</Typography>}
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
          {description}
        </Typography>
      )}
    </Box>
  );

  const grid2 = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 1,
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      {/* Header */}
      <PageBreadcrumbs
        items={[{ text: 'Goals', icon: <GoalsIcon />, link: '/goals' }, { text: 'New goal' }]}
        mb={0}
      />

      {createState?.error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => clearCreate()}>{String(createState.error)}</Alert>
      )}
      {quotaBlocked && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Your plan allows {price.goals_limit} goal{price.goals_limit !== 1 ? 's' : ''} and you&rsquo;re
          using {price.goals_used}. Delete one or upgrade.
        </Alert>
      )}

      <Box sx={{ mt: 1.5 }}>
        {/* Row 1: Goal + Tuning */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          {/* Goal */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack spacing={2.5}>
              <Box>
                <CardHead icon={<InsightsIcon fontSize="small" />} title="Goal name" />
                <TextField
                  size="small" fullWidth value={name}
                  placeholder={isFleet ? 'Name this fleet goal' : 'Name this goal'}
                  onChange={(e) => setName(e.target.value)}
                />
              </Box>

              <Box>
                <CardHead
                  icon={<FlagIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />}
                  title="What are you watching?"
                  helper="One signal gets a forecast; a fleet gets a ranked leaderboard of its members."
                />
                <Box sx={grid2}>
                  {SCOPES.map((sc) => (
                    <SelectCard
                      key={sc.id}
                      color={accent('primary')}
                      icon={sc.icon}
                      title={sc.label}
                      description={sc.blurb}
                      active={scope === sc.id}
                      onClick={() => switchScope(sc.id)}
                    />
                  ))}
                </Box>
              </Box>

              {!isFleet ? (
                <Box>
                  <CardHead
                    icon={<FlagIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />}
                    title="Signal to understand"
                    helper="The signal you want to forecast or explain."
                  />
                  <Autocomplete
                    size="small"
                    options={targetOptions}
                    value={targetKey}
                    loading={signalsLoading}
                    getOptionLabel={labelFor}
                    filterOptions={(opts, state) => {
                      const q = (state.inputValue || '').trim().toLowerCase();
                      if (!q) return opts;
                      return opts.filter((k) => labelFor(k).toLowerCase().includes(q) || (k || '').toLowerCase().includes(q));
                    }}
                    onChange={(_e, v) => setTargetKey(v)}
                    renderInput={(params) => <TextField {...params} placeholder="Pick a signal…" />}
                  />
                </Box>
              ) : (
                <Box>
                  <CardHead
                    icon={<FlagIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />}
                    title="Population to watch"
                    helper="Membership resolves fresh on every run — new signals join automatically."
                  />
                  <Box sx={{ ...grid2, mb: 1.25 }}>
                    {SELECTOR_TYPES.map((st) => (
                      <SelectCard
                        key={st.id}
                        color={accent('primary')}
                        icon={st.icon}
                        title={st.label}
                        description={st.blurb}
                        active={selectorType === st.id}
                        onClick={() => { setSelectorType(st.id); setSelectorValue(''); }}
                      />
                    ))}
                  </Box>
                  {selectorType === 'group' ? (
                    <Autocomplete
                      size="small"
                      options={groupOptions}
                      value={selectorValue || null}
                      loading={groupsLoading}
                      onChange={(_e, v) => setSelectorValue(v || '')}
                      renderInput={(params) => <TextField {...params} placeholder="Pick a signal group…" />}
                    />
                  ) : (
                    <TextField
                      size="small" fullWidth value={selectorValue}
                      placeholder="e.g. sensor_*_vibration"
                      helperText="Glob wildcards: * matches anything, ? one character."
                      onChange={(e) => setSelectorValue(e.target.value)}
                    />
                  )}

                  {/* Live membership preview */}
                  {selectorValue.trim() && (
                    <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 2, border: `1px dashed ${theme.palette.divider}` }}>
                      {fleetPreview?.isLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">Resolving members…</Typography>
                        </Stack>
                      ) : fleetPreview?.error ? (
                        <Typography variant="caption" color="error.main">{String(fleetPreview.error)}</Typography>
                      ) : memberCount != null ? (
                        <>
                          <Typography variant="body2" fontWeight={700}>
                            {memberCount.toLocaleString()} signal{memberCount === 1 ? '' : 's'} match
                            {overLimit && (
                              <Typography component="span" variant="body2" color="error.main" fontWeight={700}>
                                {' '}— above your plan&rsquo;s limit of {fleetPreviewData.members_limit?.toLocaleString?.()}
                              </Typography>
                            )}
                          </Typography>
                          {memberCount === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Nothing matches yet — pick another group or widen the pattern.
                            </Typography>
                          )}
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {(fleetPreviewData?.sample || []).map((k) => (
                              <Chip key={k} size="small" label={k} sx={{ height: 20, fontSize: 11 }} />
                            ))}
                            {memberCount > (fleetPreviewData?.sample || []).length && (
                              <Chip size="small" variant="outlined" label={`+${(memberCount - (fleetPreviewData?.sample || []).length).toLocaleString()} more`} sx={{ height: 20, fontSize: 11 }} />
                            )}
                          </Stack>
                        </>
                      ) : null}
                    </Box>
                  )}
                </Box>
              )}

              <Box>
                <CardHead
                  icon={<AutoAwesomeIcon fontSize="small" />}
                  title={isFleet ? 'What to rank by' : 'Goal Type'}
                  helper={isFleet
                    ? 'Every run scores each member and rebuilds the leaderboard.'
                    : 'Choose what you want to know about it.'}
                />
                <Box sx={grid2}>
                  {(isFleet ? FLEET_GOAL_TYPES : GOAL_TYPES).map((gt) => (
                    <SelectCard
                      key={gt.id}
                      color={accent(gt.accentKey)}
                      icon={gt.icon}
                      title={gt.label}
                      description={gt.blurb}
                      active={goalType === gt.id}
                      onClick={() => setGoalType(gt.id)}
                    />
                  ))}
                </Box>
              </Box>
            </Stack>
          </Paper>

          {/* Right column — tuning. Single: detected profile + policies once a
              signal is picked. Fleet: cadence/horizon + risk definition. */}
          {!isFleet && targetKey && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack spacing={2}>
                {/* Header with single reset affordance */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <TuneIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>Tuning</Typography>
                  </Stack>
                  {tuningDirty && (
                    <Button
                      size="small"
                      startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                      onClick={resetTuning}
                      sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0, py: 0.25 }}
                    >
                      Reset
                    </Button>
                  )}
                </Stack>

                {notAnalyzable && <Alert severity="warning">{String(preview.error)}</Alert>}

                <TextField
                  select size="small" fullWidth label="Data Cadence" value={intervalOverride ?? 'auto'}
                  onChange={(e) => setIntervalOverride(e.target.value === 'auto' ? null : Number(e.target.value))}
                  helperText="How often your data updates — one step equals one interval."
                >
                  <MenuItem value="auto">Auto{resolved ? ` (${CADENCES.find((c) => c.value === resolved.interval_seconds)?.label?.toLowerCase() || `${resolved.interval_seconds}s`})` : ''}</MenuItem>
                  {CADENCES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>

                <SpanPicker
                  icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                  title={goalCopy(goalType).horizonTitle}
                  value={effHorizon}
                  min={HORIZON_MIN}
                  max={HORIZON_MAX}
                  interval={effInterval}
                  overridden={horizonOverride != null}
                  onChange={(v) => setHorizonOverride(v)}
                />

                <SpanPicker
                  icon={<HistoryIcon sx={{ fontSize: 16 }} />}
                  title="History to look back on"
                  value={effLag}
                  min={LAG_MIN}
                  max={LAG_MAX}
                  interval={effInterval}
                  overridden={maxLagOverride != null}
                  onChange={(v) => setMaxLagOverride(v)}
                />

                <Divider />

                <TextField
                  select size="small" fullWidth label="Driver discovery"
                  value={discoveryMode} onChange={(e) => setDiscoveryMode(e.target.value)}
                  helperText={DISCOVERY_MODES.find((m) => m.value === discoveryMode)?.help}
                >
                  {DISCOVERY_MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
                {discoveryMode === 'scheduled' && (
                  <TextField
                    select size="small" fullWidth label="Discovery cadence"
                    value={discoveryFreq} onChange={(e) => setDiscoveryFreq(Number(e.target.value))}
                  >
                    {FREQUENCIES.map((f) => (
                      <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                    ))}
                  </TextField>
                )}

                <TextField
                  select size="small" fullWidth label="Retraining"
                  value={retrainMode} onChange={(e) => setRetrainMode(e.target.value)}
                  helperText={RETRAIN_MODES.find((m) => m.value === retrainMode)?.help}
                >
                  {RETRAIN_MODES.map((r) => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </TextField>
                {retrainMode === 'scheduled' && (
                  <TextField
                    select size="small" fullWidth label="Retraining cadence"
                    value={retrainFreq} onChange={(e) => setRetrainFreq(Number(e.target.value))}
                  >
                    {FREQUENCIES.map((f) => (
                      <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                    ))}
                  </TextField>
                )}

                <TextField
                  select size="small" fullWidth label="Forecasting"
                  value={servingMode} onChange={(e) => setServingMode(e.target.value)}
                  helperText={SERVING_MODES.find((m) => m.value === servingMode)?.help}
                >
                  {SERVING_MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
                {servingMode === 'scheduled' && (
                  <TextField
                    select size="small" fullWidth label="Forecast cadence"
                    value={servingFreq} onChange={(e) => setServingFreq(Number(e.target.value))}
                  >
                    {CADENCES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </TextField>
                )}

                <TextField
                  size="small" fullWidth type="number" label={BUDGET_LABEL}
                  value={monthlyBudget} onChange={(e) => setMonthlyBudget(e.target.value)} placeholder="Plan default"
                />
              </Stack>
            </Paper>
          )}

          {isFleet && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <TuneIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Tuning</Typography>
                </Stack>

                <TextField
                  select size="small" fullWidth label="Data cadence" value={fleetInterval}
                  onChange={(e) => setFleetInterval(Number(e.target.value))}
                  helperText="How often member signals update — one step equals one interval."
                >
                  {CADENCES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>

                <SpanPicker
                  icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                  title="How far ahead to look"
                  value={fleetHorizon}
                  min={HORIZON_MIN}
                  max={HORIZON_MAX}
                  interval={fleetInterval}
                  overridden
                  onChange={(v) => setFleetHorizon(v)}
                />

                <TextField
                  select size="small" fullWidth label="Scoring cadence"
                  value={runInterval ?? 'auto'}
                  onChange={(e) => setRunInterval(e.target.value === 'auto' ? null : Number(e.target.value))}
                  helperText="How often the whole fleet is re-scored and the leaderboard rebuilt."
                >
                  <MenuItem value="auto">Every data interval</MenuItem>
                  {RUN_CADENCES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>

                {goalType === 'rank_by_risk' && (
                  <>
                    <Divider />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                      Risk definition — what counts as a breach for a member.
                    </Typography>
                    <TextField
                      select size="small" fullWidth label="Risky direction" value={riskDirection}
                      onChange={(e) => setRiskDirection(e.target.value)}
                      helperText={riskDirection === 'above'
                        ? 'High values are bad (vibration, temperature, error rate).'
                        : 'Low values are bad (throughput, battery, liquidity).'}
                    >
                      <MenuItem value="above">Spikes above threshold</MenuItem>
                      <MenuItem value="below">Drops below threshold</MenuItem>
                    </TextField>
                    <TextField
                      select size="small" fullWidth label="Threshold" value={thresholdMode}
                      onChange={(e) => setThresholdMode(e.target.value)}
                      helperText={thresholdMode === 'auto'
                        ? 'Each member gets its own threshold from its recent behaviour — fair across different scales.'
                        : 'One absolute level for the whole fleet — only sensible when members share a unit.'}
                    >
                      <MenuItem value="auto">Auto (per member)</MenuItem>
                      <MenuItem value="absolute">Absolute level</MenuItem>
                    </TextField>
                    {thresholdMode === 'absolute' && (
                      <TextField
                        size="small" fullWidth type="number" label="Threshold level"
                        value={thresholdValue}
                        onChange={(e) => setThresholdValue(e.target.value)}
                      />
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          )}
        </Box>

        {/* Row 2 — single: the model lineup. Fleet: models are picked FOR the
            fleet by the bake-off, so the row explains that instead. */}
        {!isFleet ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
            <ModelLineupPicker
              key={goalType || 'none'}
              models={models}
              modelsLoading={modelsLoading}
              blueprints={blueprints}
              blueprintsLoading={blueprintsLoading}
              deselected={deselected}
              bpSelected={bpSelected}
              onChange={onModelsChange}
              onEnterPick={blueprintsFetch}
              ready={ready}
            />
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <EmojiEventsIcon sx={{ color: theme.palette.warning.main, mt: 0.25 }} fontSize="small" />
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Model selection is automatic — the bake-off
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  On creation, every eligible zero-shot foundation model scores a sample
                  of your fleet against held-out history, and the best RANKER — not the
                  best point forecaster — wins the fleet. The first leaderboard follows
                  right after, typically within minutes. You can review the results on
                  the goal&rsquo;s Receipts tab.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {/* Row 3: Cost estimate — shared CostEstimateCard, same box as
            pipeline creation so billing expectations read identically
            everywhere. */}
        {ready && !isFleet && (
          <CostEstimateCard
            sx={{ mt: 2 }}
            title="Cost estimator"
            infoTip="Estimates what this goal costs based on your plan, the models you picked, and the detected data cadence. Uses live pricing from your account."
            billingModes={['charged_today', 'monthly_recurring', 'usage_based']}
            stats={[
              { label: 'Setup — today', value: setupCost },
              { label: 'Estimated / month', value: monthlyRecurring, accent: true, suffix: '~' },
              { label: 'Auto-retrain cap / mo', value: retrainCap, suffix: '≤' },
            ]}
            balance={price?.balance != null ? price.balance : null}
            coverage={insufficient
              ? {
                tier: 'insufficient',
                detail: `Your balance of ${Math.floor(price.balance)} credits won't cover today's `
                  + `setup (${setupCost} credits). Add at least ${Math.ceil(setupCost - price.balance)} `
                  + 'more credits to start discovery.',
              }
              : { tier: 'ok', chipLabel: null }}
            footerRight={(
              <Button
                variant="contained"
                startIcon={createState?.isLoading ? <CircularProgress size={16} color="inherit" /> : <AddTaskIcon />}
                disabled={!canStart}
                onClick={submit}
              >
                {createState?.isLoading ? 'Starting…' : 'Start discovery'}
              </Button>
            )}
            explainTitle="Cost breakdown"
            explainFootnote="Estimates are in credits. Storage is billed separately."
            explainContent={(
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                <ExplainSectionTitle>Today — one-time setup</ExplainSectionTitle>
                <BreakdownRow
                  label="Discovery scan"
                  hint="One scan to find which signals drive your target."
                  value={`${runCost} cr`}
                />
                <BreakdownRow
                  label="Model fitting"
                  hint="Scales with the models you pick — fewer or lighter models cost less."
                  value={`${fitCost} cr`}
                />
                {selectionFactor < 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                    You picked a lighter set — fitting is {Math.round(selectionFactor * 100)}% of the
                    full lineup ({baseFitCost} cr).
                  </Typography>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body2" fontWeight={600}>Charged today</Typography>
                  <Typography variant="body2" fontWeight={700}>{setupCost} cr</Typography>
                </Stack>

                <Divider />
                <ExplainSectionTitle>Every month — ongoing</ExplainSectionTitle>
                <BreakdownRow
                  label="Live monitoring"
                  hint="Predictable daily rate per live goal — covers drift monitoring, automatic driver re-validation and shock handling."
                  value={`${liveMonthly} cr`}
                />
                {dedicatedWorthIt ? (
                  <BreakdownRow
                    label="Live predictions (dedicated deployment)"
                    hint={`At ~${predictionsPerMonth.toLocaleString()} forecasts/mo (every ${fmtSpan(1, serveEverySec)}), a dedicated pod is cheaper — flat hosting with all predictions included.`}
                    value={`${dedicatedPredictMonthly.toLocaleString()} cr`}
                  />
                ) : (
                  <BreakdownRow
                    label="Live predictions"
                    hint={`The champion re-forecasts every ${fmtSpan(1, serveEverySec)} as data arrives (~${predictionsPerMonth.toLocaleString()}/mo), billed per forecast on shared infrastructure (${inferenceCost} cr each).`}
                    value={`${sharedPredictMonthly.toLocaleString()} cr`}
                  />
                )}
                <BreakdownRow
                  label="Automatic re-discovery"
                  hint="A full driver scan runs when new signals show promise (or on your schedule) — estimated at about one per month."
                  value={`~${rediscoverMonthly} cr`}
                />
                <BreakdownRow
                  label="Auto-retraining"
                  hint={`Triggered as performance drifts (~${retrainCost} cr each), billed as used and capped by your monthly budget.`}
                  value={`up to ${retrainCap} cr`}
                />

                <Divider />
                <ExplainSectionTitle>Serving — pay per prediction vs dedicated deployment</ExplainSectionTitle>
                <Typography variant="caption" color="text.secondary">
                  The champion re-forecasts every {fmtSpan(1, serveEverySec)} as new data arrives
                  — ~{predictionsPerMonth.toLocaleString()} forecasts/mo. Each forecast already covers
                  the full {fmtSpan(effHorizon, effInterval)} horizon in one inference, so the horizon
                  doesn&rsquo;t change this count.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 0.5 }}>
                  {[
                    {
                      key: 'shared',
                      title: 'Pay per prediction',
                      sub: 'Shared infrastructure',
                      amount: sharedPredictMonthly,
                      best: !dedicatedWorthIt,
                    },
                    {
                      key: 'dedicated',
                      title: 'Dedicated deployment',
                      sub: 'Dedicated pod — predictions included',
                      amount: dedicatedPredictMonthly,
                      best: dedicatedWorthIt,
                    },
                  ].map((opt) => (
                    <Box
                      key={opt.key}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: (t) => `1px solid ${opt.best ? t.palette.primary.main : t.palette.divider}`,
                        bgcolor: (t) => (opt.best ? alpha(t.palette.primary.main, 0.08) : 'transparent'),
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                        <Typography variant="body2" fontWeight={700}>{opt.title}</Typography>
                        {opt.best && (
                          <Chip size="small" label="Recommended" color="primary" sx={{ height: 16, fontSize: 9, fontWeight: 700 }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {opt.sub}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {opt.amount.toLocaleString()} cr/mo
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', opacity: 0.85 }}>
                  {dedicatedWorthIt
                    ? `At this volume we recommend a dedicated deployment — it saves ~${(sharedPredictMonthly - dedicatedPredictMonthly).toLocaleString()} cr/mo (the estimate above uses it). Choose "Dedicated" when you deploy the champion.`
                    : 'We recommend pay-per-prediction at this volume (the estimate above uses it). A dedicated deployment only pays off at higher prediction volume.'}
                </Typography>

                {(deselected.size > 0 || bpSelected.size > 0) && selectedModels.length > 0 && (
                  <Box>
                    <ExplainSectionTitle>Models in this fit</ExplainSectionTitle>
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {selectedModels.map((m) => (
                        <Stack key={m.id} direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" noWrap sx={{ mr: 1 }}>{modelDisplayName(m)}</Typography>
                          <Chip
                            size="small"
                            icon={<BoltRoundedIcon sx={{ fontSize: 11 }} />}
                            label={`${m.cost_weight}\u00d7`}
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}

                {meteredSelected && (
                  <Alert severity="info" icon={<BoltRoundedIcon fontSize="small" />} sx={{ py: 0.25 }}>
                    Foundation models run on GPU and add usage at fit time, billed separately.
                  </Alert>
                )}
              </Stack>
            )}
          />
        )}

        {ready && isFleet && (
          <CostEstimateCard
            sx={{ mt: 2 }}
            title="Cost estimator"
            infoTip="Estimates what this fleet costs based on its size and scoring cadence. Uses live pricing from your account."
            billingModes={['charged_today', 'monthly_recurring']}
            stats={[
              { label: 'Setup — today', value: bakeoffCost },
              { label: 'Estimated / month', value: fleetMonthly, accent: true, suffix: '~' },
            ]}
            balance={price?.balance != null ? price.balance : null}
            coverage={fleetInsufficient
              ? {
                tier: 'insufficient',
                detail: `Your balance of ${Math.floor(price.balance)} credits won't cover today's `
                  + `setup (${bakeoffCost} credits). Add at least ${Math.ceil(bakeoffCost - price.balance)} `
                  + 'more credits to start.',
              }
              : { tier: 'ok', chipLabel: null }}
            footerRight={(
              <Button
                variant="contained"
                startIcon={createState?.isLoading ? <CircularProgress size={16} color="inherit" /> : <AddTaskIcon />}
                disabled={!canStart}
                onClick={submit}
              >
                {createState?.isLoading ? 'Starting…' : 'Start fleet'}
              </Button>
            )}
            explainTitle="Cost breakdown"
            explainFootnote="Estimates are in credits. Storage is billed separately."
            explainContent={(
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                <ExplainSectionTitle>Today — one-time setup</ExplainSectionTitle>
                <BreakdownRow
                  label="Model bake-off"
                  hint="Every eligible foundation model scores a member sample; the best ranker becomes this fleet's champion."
                  value={`${bakeoffCost} cr`}
                />
                <Divider />
                <ExplainSectionTitle>Every month — ongoing</ExplainSectionTitle>
                <BreakdownRow
                  label="Fleet scoring runs"
                  hint={`${memberCount?.toLocaleString?.() || memberCount} members re-scored every ${fmtSpan(1, runEverySec)} (~${runsPerMonth.toLocaleString()} runs/mo at ${fleetRunCost} cr each). Cost scales with fleet size.`}
                  value={`~${fleetMonthly.toLocaleString()} cr`}
                />
                <Typography variant="caption" color="text.secondary">
                  Includes the aggregate driver discovery, movement tracking, receipts
                  verification and digest alerts — no per-member charges.
                </Typography>
              </Stack>
            )}
          />
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  // /signal/overview returns a view payload; the full signal ``catalog``
  // (every numeric key + display name, independent of the chart's current
  // drill level) is what the target picker needs.
  signals: state.signal_overview?.response?.catalog || [],
  signalsLoading: Boolean(state.signal_overview?.isLoading),
  groups: state.signal_groups?.response?.groups || [],
  groupsLoading: Boolean(state.signal_groups?.isLoading),
  createState: state.discovery_create_goal,
  price: state.discovery_price?.response,
  preview: state.discovery_profile_preview,
  fleetPreview: state.discovery_fleet_preview,
  models: state.discovery_models?.response?.models || [],
  modelsLoading: Boolean(state.discovery_models?.isLoading),
  blueprints: state.blueprints_list?.response?.models || [],
  blueprintsLoading: Boolean(state.blueprints_list?.isLoading),
});

const mapDispatchToProps = (dispatch) => ({
  fetchSignals: () => dispatch(fetchOverview()),
  groupsFetch: () => dispatch(fetchGroups()),
  createGoal: (payload) => dispatch(createGoalRequest(payload)),
  clearCreate: () => dispatch(clearCreateGoal()),
  priceFetch: () => dispatch(fetchPrice()),
  previewFetch: (payload) => dispatch(fetchProfilePreview(payload)),
  previewClear: () => dispatch(clearProfilePreview()),
  fleetPreviewFetch: (payload) => dispatch(fetchFleetPreview(payload)),
  fleetPreviewClear: () => dispatch(clearFleetPreview()),
  modelsFetch: (payload) => dispatch(fetchModels(payload)),
  blueprintsFetch: () => dispatch(fetchBlueprints()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateGoalPage);
